import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import Dropzone from '../../misc/Dropzone';
import Icon from '../../misc/Icon';
import FieldStyleSettings from './FieldStyleSettings';
import MetadataSettings from './MetadataSettings';
import ConditionalLogicSettings from './ConditionalLogicSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import SettingsLabel from './SettingsLabel';
import { StyledAddonAfter, StyledFieldWrapper, StyledButtonsWrapper, DividerLine } from './SettingsCommonElements';

import Button from '../../misc/Button';
import PageLayoutModes from '../../../constants/PageLayoutModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';
import FileViews from '../../../constants/FileViews';
import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';
import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';
import { getPageDeletable, getPageCopyable } from '../../../lib/documentHelper';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const PageSettings = ({

  view,
  page,
  pageIndex,
  config,
  hidden,

  //Logic properties
  fileId,
  sortedPages,
  fields,

  //Action handlers
  onChange,
  onUpload,
  onUploadDelete,
  onDelete,

}) => {

  const theme = useContext(ThemeContext);

  const {
    _id,
    layout,
    presentation,
    cols,
    width,
    height,
    rowHeight,
    padding,
    metadata,
    logic,
    backgroundImage,
    backgroundSize
  } = page;

  //Settings
  const isTitleHidden = isSettingHidden(config, 'title');
  const isHiddenHidden = isSettingHidden(config, 'hidden');
  const isLogicHidden = isSettingHidden(config, 'logic');

  const isBackgroundImageHidden = isStyleSettingHidden(config, 'backgroundImage');
  const isBackgroundSizeHidden = isStyleSettingHidden(config, 'backgroundSize');
  const isWidthHidden = isStyleSettingHidden(config, 'width');
  const isHeightHidden = isStyleSettingHidden(config, 'height');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');
  const isPresentationHidden = isStyleSettingHidden(config, 'presentation');
  const isLayoutHidden = isStyleSettingHidden(config, 'layout');
  const isColumnsHidden = isStyleSettingHidden(config, 'columns');
  const isRowHeightHidden = isStyleSettingHidden(config, 'rowHeight');

  const metadataOptions = getMetadataOptions(config);

  const settingsNode = (
    <>
      {isTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            value={page.name}
            type="text"
            onChange={(fieldId, value) => onChange(_id, { name: value })}
          />
        </StyledFieldWrapper>
      )}
      {isBackgroundImageHidden || !onUpload || view === FileViews.mobile ? null : (
        <>
          <StyledFieldWrapper>
            <Dropzone
              dragMessage="pdf, png, jpg, ..."
              onDrop={(files) => onUpload(_id, files[0])}
              accept={{
                'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
                'image/png': ['.png', '.PNG'],
                'application/pdf': ['.pdf', '.PDF']
              }}
            />
          </StyledFieldWrapper>
          {!backgroundImage ? null : (
            <StyledButtonsWrapper>
              <Button
                danger
                text="Remove Background"
                styles={{ marginBottom: '12px' }}
                icon={ThemeIcons.settings.delete}
                onClick={() => onUploadDelete(_id)}
              />
            </StyledButtonsWrapper>
          )}
        </>
      )}

      {(isHiddenHidden && isLogicHidden) || sortedPages.length < 2 ? null : (
        <>
          <SettingsLabel>Conditional Logic</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
          {isLogicHidden || sortedPages.length < 2 ? null : (
            <StyledFieldWrapper>
              <ConditionalLogicSettings
                fileId={fileId}
                targetLabel="page"
                targetPageId={page._id}
                sortedPages={sortedPages}
                fields={fields}
                logic={logic}
                onSubmit={(nextLogic) => onChange(_id, { logic: nextLogic })}
              />
            </StyledFieldWrapper>
          )}
          {isHiddenHidden ? null : (
            <StyledFieldWrapper>
              <CheckboxSettingsField
                icon={ThemeIcons.settings.hide}
                iconColor={hidden ? theme.warningColor : theme.borderColor}
                id="hidden"
                label="Hidden"
                selected={hidden}
                onChange={(selected) => onChange(_id, { hidden: selected })}
              />
            </StyledFieldWrapper>
          )}
        </>
      )}
      {!metadataOptions ? null : (
        <>
          <DividerLine />
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(_id, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {(
        isPaddingHidden
        && isBackgroundSizeHidden
        && isWidthHidden
        && isHeightHidden
      ) ? null : (
        <>
          <SettingsLabel>Styles</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
        )}
      {isWidthHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            value={width}
            type="number"
            addonAfter={<StyledAddonAfter>Width</StyledAddonAfter>}
            onChange={(fieldId, value) => onChange(_id, { width: value })}
          />
        </StyledFieldWrapper>
      )}
      {isHeightHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            value={height}
            type="number"
            addonAfter={<StyledAddonAfter>Height</StyledAddonAfter>}
            onChange={(fieldId, value) => onChange(_id, { height: value })}
          />
        </StyledFieldWrapper>
      )}
      <StyledFieldWrapper style={{ marginBottom: 6 }}>
        <FieldStyleSettings
          id="page-field"
          {...{

            padding,
            onPaddingChange: isPaddingHidden ? null : (value) => onChange(_id, { padding: value }),

          }}
        />
      </StyledFieldWrapper>
      {isBackgroundSizeHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            value={backgroundSize}
            addonAfter={<StyledAddonAfter>Bg Resize</StyledAddonAfter>}
            options={[
              {
                _id: '100% 100%',
                value: 'Stretch',
              },
              {
                _id: '',
                value: 'Auto',
              },
            ]}
            onChange={(i, value) => onChange(_id, { backgroundSize: value })}
          />
        </StyledFieldWrapper>
      )}
      {(
        isPresentationHidden
        && isLayoutHidden
        && isColumnsHidden
        && isRowHeightHidden
      ) ? null : (
        <>
          <SettingsLabel>Layout</SettingsLabel>
          <DividerLine style={{ marginBottom: 14 }} />
        </>
        )}
      {isPresentationHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            value={presentation}
            addonAfter={<StyledAddonAfter>Field</StyledAddonAfter>}
            options={[
              {
                _id: FieldPresentationModes.normal,
                value: 'Normal',
              },
              {
                _id: FieldPresentationModes.transparent,
                value: 'Transparent',
              },
            ]}
            onChange={(i, value) => onChange(_id, { presentation: value })}
          />
        </StyledFieldWrapper>
      )}
      {isLayoutHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            type="dropdown"
            value={layout}
            addonAfter={<StyledAddonAfter>Layout</StyledAddonAfter>}
            options={[
              {
                _id: PageLayoutModes.grid,
                value: 'Grid',
              },
              {
                _id: PageLayoutModes.float,
                value: 'Float',
              },
            ]}
            onChange={(i, value) => onChange(_id, { layout: value })}
          />
        </StyledFieldWrapper>
      )}
      {isColumnsHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            id="grid-column-count"
            value={cols}
            addonBefore={<Icon icon={ThemeIcons.settings.pageColumns} />}
            addonAfter={<StyledAddonAfter>Columns</StyledAddonAfter>}
            type="number"
            onChange={(fieldId, value) => {
              if (value) onChange(_id, { cols: parseInt(value, 10) });
            }}
          />
        </StyledFieldWrapper>
      )}
      {isRowHeightHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            id="grid-row-height"
            value={rowHeight}
            addonBefore={<Icon icon={ThemeIcons.settings.pageRowHeight} />}
            addonAfter={<StyledAddonAfter>Row Height</StyledAddonAfter>}
            type="number"
            onChange={(fieldId, value) => {
              if (value) onChange(_id, { rowHeight: parseInt(value, 10) });
            }}
          />
        </StyledFieldWrapper>
      )}
      <DividerLine />
      <>
        <SettingsLabel>Actions</SettingsLabel>
        <DividerLine style={{ marginBottom: 14 }} />
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="deletable"
            icon={ThemeIcons.settings.delete}
            iconColor={theme.dangerColor}
            label="Deletable"
            selected={getPageDeletable(page)}
            onChange={(selected) => onChange(_id, { deletable: selected })}
          />
        </StyledFieldWrapper>
        <SettingsLabel>Copyable</SettingsLabel>
        <DividerLine style={{ marginBottom: 14 }} />
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="copyable-none"
            label="Not Copyable"
            selected={getPageCopyable(page).includes('none')}
            onChange={(selected) => {
              // Selecting "Not Copyable" clears all other options (exclusive)
              onChange(_id, { copyable: selected ? ['none'] : ['with-values'] });
            }}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="copyable-with-values"
            label="With Values"
            selected={getPageCopyable(page).includes('with-values')}
            onChange={(selected) => {
              const next = selected
                ? [...getPageCopyable(page).filter(v => v !== 'none' && v !== 'with-values'), 'with-values']
                : getPageCopyable(page).filter(v => v !== 'with-values');
              onChange(_id, { copyable: next.length > 0 ? next : ['with-values'] });
            }}
          />
        </StyledFieldWrapper>
        <StyledFieldWrapper>
          <CheckboxSettingsField
            id="copyable-without-values"
            label="Without Values"
            selected={getPageCopyable(page).includes('without-values')}
            onChange={(selected) => {
              const next = selected
                ? [...getPageCopyable(page).filter(v => v !== 'none' && v !== 'without-values'), 'without-values']
                : getPageCopyable(page).filter(v => v !== 'without-values');
              onChange(_id, { copyable: next.length > 0 ? next : ['with-values'] });
            }}
          />
        </StyledFieldWrapper>
      </>
      {!onDelete ? null : (
        <>
          <DividerLine />
          <StyledButtonsWrapper>
            <Button
              danger
              icon={ThemeIcons.settings.delete}
              text="Delete"
              onClick={() => onDelete(page._id)}
            />
          </StyledButtonsWrapper>
        </>
      )}
    </>
  );

  return (
    <StyledWrapper>
      <FieldSettingsHeaderTitle
        title={`Page ${pageIndex + 1} Settings`}
      />
      {settingsNode}
    </StyledWrapper>
  );
};

PageSettings.propTypes = {
  page: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  onUpload: PropTypes.func,
  onDelete: PropTypes.func
};

export default PageSettings;
